import { useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { replaceMyPblFirmAuthorisation } from '@/api/pbl.api';
import { FormTitle, InputWrapper } from '@/components/Container/AuthContainer/styles';
import { Button } from '@/components/UI/Button';
import { ErrorMessage, Text } from '@/components/UI/Typography/Text';
import type { PblStorageFile } from '@/types/pbl';
import { errorHandler, successHandler } from '@/utils/toastHandler';
import { FormSection } from './styles';

type Props = {
  currentFile: PblStorageFile | null;
  onUploaded: (file: PblStorageFile) => void;
  onUploading: (uploading: boolean) => void;
  disabled: boolean;
};

const FirmAuthorisationUpload = ({ currentFile, onUploaded, onUploading, disabled }: Props) => {
  // @ts-ignore -- the legacy translation catalog exceeds TypeScript's recursive key inference limit
  const { t } = useTranslation();
  const tr = (key: string) => t(`pbl.backlogEnhancements.cv.firmAuthorisation.${key}` as any) as string;
  const [uploading, setUploading] = useState(false);
  const uploadingRef = useRef(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const { control, handleSubmit, reset, watch } = useForm<{ file: File | null }>({ defaultValues: { file: null } });
  const selectedFile = watch('file');

  const upload = handleSubmit(async ({ file }) => {
    if (!file || disabled || uploadingRef.current) return;
    uploadingRef.current = true;
    setUploading(true);
    onUploading(true);
    try {
      const response = await replaceMyPblFirmAuthorisation(file);
      if (!response.data.success || !response.data.data?.id) throw new Error(tr('uploadFailed'));
      onUploaded(response.data.data);
      reset({ file: null });
      if (inputRef.current) inputRef.current.value = '';
      successHandler(tr('uploaded'));
    } catch (error: any) {
      errorHandler(error.response?.data?.message || tr('uploadFailed'));
    } finally {
      uploadingRef.current = false;
      setUploading(false);
      onUploading(false);
    }
  });

  return (
    <FormSection aria-labelledby="firm-authorisation-title">
      <FormTitle id="firm-authorisation-title">{tr('title')}</FormTitle>
      <Text>{tr('currentFile')}: {currentFile?.name || tr('noFile')}</Text>
      <Text id="firm-authorisation-help">{tr('help')}</Text>
      <InputWrapper>
        <Text as="label" htmlFor="firm-authorisation-file">{tr('replacementFile')}</Text>
        <Controller
          name="file"
          control={control}
          rules={{
            validate: (file) => {
              if (!file) return tr('required');
              if (!/\.pdf$/i.test(file.name) || (file.type && file.type !== 'application/pdf')) return tr('pdfOnly');
              return file.size <= 10 * 1024 * 1024 || tr('tooLarge');
            },
          }}
          render={({ field, fieldState }) => (
            <>
              <input
                id="firm-authorisation-file"
                type="file"
                accept=".pdf,application/pdf"
                name={field.name}
                ref={(element) => { field.ref(element); inputRef.current = element; }}
                onBlur={field.onBlur}
                onChange={(event) => field.onChange(event.target.files?.[0] || null)}
                disabled={disabled || uploading}
                aria-invalid={!!fieldState.error}
                aria-describedby={`firm-authorisation-help${fieldState.error ? ' firm-authorisation-error' : ''}`}
              />
              {fieldState.error ? <ErrorMessage id="firm-authorisation-error" role="alert">{fieldState.error.message}</ErrorMessage> : null}
            </>
          )}
        />
      </InputWrapper>
      <Button type="button" size="md" variant="primary" rounded={false}
        disabled={disabled || uploading || !selectedFile} onClick={() => upload()}>
        {tr(uploading ? 'uploading' : 'upload')}
      </Button>
    </FormSection>
  );
};

export default FirmAuthorisationUpload;
